import openai from '../lib/openaiClient';
import { supabase } from '../lib/supabase';

/**
 * AI Review Service - Generates realistic product reviews using OpenAI
 * and manages affiliate links for monetization
 */

/**
 * Validates OpenAI API key is configured
 * @throws {Error} If API key is missing or invalid
 */
function validateOpenAIKey() {
  const apiKey = import.meta.env?.VITE_OPENAI_API_KEY;
  if (!apiKey || apiKey === 'your-openai-api-key-here' || apiKey?.trim() === '') {
    throw new Error('OpenAI API key is not configured. Please add VITE_OPENAI_API_KEY to your .env file.');
  }
}

/**
 * Generates AI-powered professional product review and buying guide
 * @param {Object} product - Product object with details
 * @returns {Promise<Object>} Generated product review content
 */
export async function generateProductReview(product) {
  try {
    // Validate API key before making request
    validateOpenAIKey();

    const prompt = `Create a comprehensive, professional product review and buying guide for the following vacuum cleaner. This is affiliate content for a product review website.
    
    Product Details:
    - Name: ${product?.name}
    - Brand: ${product?.brand}
    - Category: ${product?.category}
    - Price: $${product?.currentPrice}
    - Description: ${product?.description}
    
    Generate a complete product review with the following sections:
    
    1. TITLE: Catchy, SEO-friendly review title (e.g., "[Brand] [Model] Review: [Key Benefit]")
    
    2. SUMMARY: 2-3 sentence overview highlighting the product's main strengths and ideal use case
    
    3. FULL REVIEW: Comprehensive 800-1000 word review covering:
       - Design and build quality
       - Performance across different surfaces
       - Key features and technology
       - Ease of use and maintenance
       - Battery life (if cordless) or power efficiency
       - Noise levels
       - Storage and portability
       - Real-world testing results
    
    4. BUYING GUIDE: 400-500 word guide including:
       - Who should buy this product
       - Who should skip it
       - What to consider before purchasing
       - Alternative options to consider
       - Best time to buy / deal recommendations
    
    5. EXPERT VERDICT: 3-4 sentence final recommendation
    
    6. BEST FOR: Array of 3-5 specific use cases (e.g., "Pet owners with multiple cats", "Large homes with hardwood floors")
    
    7. KEY FEATURES: Array of 5-7 standout features with brief explanations
    
    8. PROS: Array of 5-7 major advantages with title and detailed description for each
    
    9. CONS: Array of 3-5 limitations or drawbacks with title and detailed description for each
    
    10. PERFORMANCE ANALYSIS: Detailed breakdown with scores (0-10) for:
       - Carpet cleaning
       - Hard floor cleaning
       - Pet hair removal
       - Edge cleaning
       - Maneuverability
       - Noise level
       - Battery life (if applicable)
       - Ease of maintenance
    
    11. VALUE ASSESSMENT: 200-300 word analysis of whether the product is worth its price
    
    12. COMPARISON CONTEXT: Brief comparison to 2-3 similar products in the market
    
    Write in an authoritative, helpful tone. Be honest about both strengths and weaknesses. Focus on practical insights that help buyers make informed decisions.`;

    const response = await openai?.chat?.completions?.create({
      model: 'gpt-4o',
      messages: [
        { 
          role: 'system', 
          content: 'You are an expert product reviewer and consumer technology journalist with 10+ years of experience testing vacuum cleaners. You write comprehensive, honest, and helpful reviews that combine technical analysis with practical insights. Your reviews are trusted by consumers making purchasing decisions.' 
        },
        { role: 'user', content: prompt },
      ],
      response_format: {
        type: 'json_schema',
        json_schema: {
          name: 'product_review',
          schema: {
            type: 'object',
            properties: {
              title: { type: 'string' },
              summary: { type: 'string' },
              fullReview: { type: 'string' },
              buyingGuide: { type: 'string' },
              expertVerdict: { type: 'string' },
              bestFor: {
                type: 'array',
                items: { type: 'string' }
              },
              keyFeatures: {
                type: 'array',
                items: {
                  type: 'object',
                  properties: {
                    feature: { type: 'string' },
                    description: { type: 'string' }
                  },
                  required: ['feature', 'description']
                }
              },
              pros: {
                type: 'array',
                items: {
                  type: 'object',
                  properties: {
                    title: { type: 'string' },
                    description: { type: 'string' }
                  },
                  required: ['title', 'description']
                }
              },
              cons: {
                type: 'array',
                items: {
                  type: 'object',
                  properties: {
                    title: { type: 'string' },
                    description: { type: 'string' }
                  },
                  required: ['title', 'description']
                }
              },
              performanceAnalysis: {
                type: 'object',
                properties: {
                  carpetCleaning: { type: 'number', minimum: 0, maximum: 10 },
                  hardFloorCleaning: { type: 'number', minimum: 0, maximum: 10 },
                  petHairRemoval: { type: 'number', minimum: 0, maximum: 10 },
                  edgeCleaning: { type: 'number', minimum: 0, maximum: 10 },
                  maneuverability: { type: 'number', minimum: 0, maximum: 10 },
                  noiseLevel: { type: 'number', minimum: 0, maximum: 10 },
                  batteryLife: { type: 'number', minimum: 0, maximum: 10 },
                  easeOfMaintenance: { type: 'number', minimum: 0, maximum: 10 }
                },
                required: ['carpetCleaning', 'hardFloorCleaning', 'petHairRemoval', 'edgeCleaning', 'maneuverability', 'noiseLevel', 'batteryLife', 'easeOfMaintenance']
              },
              valueAssessment: { type: 'string' },
              comparisonContext: { type: 'string' }
            },
            required: ['title', 'summary', 'fullReview', 'buyingGuide', 'expertVerdict', 'bestFor', 'keyFeatures', 'pros', 'cons', 'performanceAnalysis', 'valueAssessment', 'comparisonContext'],
            additionalProperties: false
          }
        }
      },
    });

    const reviewContent = JSON.parse(response?.choices?.[0]?.message?.content);
    return reviewContent;
  } catch (error) {
    console.error('Error generating product review:', error);
    throw error;
  }
}

/**
 * Saves AI-generated product review to the database
 * @param {string} productId - Product UUID
 * @param {Object} reviewContent - Review content object
 * @returns {Promise<Object>} Saved review data
 */
export async function saveProductReview(productId, reviewContent) {
  try {
    // Check if product review already exists
    const { data: existing } = await supabase
      ?.from('product_reviews')
      ?.select('id')
      ?.eq('product_id', productId)
      ?.maybeSingle();

    if (existing) {
      // Update existing review
      const { data, error } = await supabase
        ?.from('product_reviews')
        ?.update({
          title: reviewContent?.title,
          summary: reviewContent?.summary,
          full_review: reviewContent?.fullReview,
          buying_guide: reviewContent?.buyingGuide,
          expert_verdict: reviewContent?.expertVerdict,
          best_for: reviewContent?.bestFor,
          key_features: reviewContent?.keyFeatures,
          performance_analysis: reviewContent?.performanceAnalysis,
          value_assessment: reviewContent?.valueAssessment,
          comparison_context: reviewContent?.comparisonContext,
          is_published: true,
          published_at: new Date()?.toISOString(),
          updated_at: new Date()?.toISOString()
        })
        ?.eq('id', existing?.id)
        ?.select()
        ?.single();

      if (error) throw error;
      
      // Save pros and cons to product_pros_cons table
      await saveProductProsAndCons(productId, reviewContent?.pros, reviewContent?.cons);
      
      return { data, isNew: false };
    } else {
      // Insert new review
      const { data, error } = await supabase
        ?.from('product_reviews')
        ?.insert([{
          product_id: productId,
          title: reviewContent?.title,
          summary: reviewContent?.summary,
          full_review: reviewContent?.fullReview,
          buying_guide: reviewContent?.buyingGuide,
          expert_verdict: reviewContent?.expertVerdict,
          best_for: reviewContent?.bestFor,
          key_features: reviewContent?.keyFeatures,
          performance_analysis: reviewContent?.performanceAnalysis,
          value_assessment: reviewContent?.valueAssessment,
          comparison_context: reviewContent?.comparisonContext,
          is_published: true,
          published_at: new Date()?.toISOString()
        }])
        ?.select()
        ?.single();

      if (error) throw error;
      
      // Save pros and cons to product_pros_cons table
      await saveProductProsAndCons(productId, reviewContent?.pros, reviewContent?.cons);
      
      return { data, isNew: true };
    }
  } catch (error) {
    console.error('Error saving product review:', error);
    throw error;
  }
}

/**
 * Saves pros and cons to product_pros_cons table
 * @param {string} productId - Product UUID
 * @param {Array} pros - Array of pros objects with title and description
 * @param {Array} cons - Array of cons objects with title and description
 * @returns {Promise<void>}
 */
export async function saveProductProsAndCons(productId, pros, cons) {
  try {
    // Delete existing pros and cons for this product
    await supabase
      ?.from('product_pros_cons')
      ?.delete()
      ?.eq('product_id', productId);

    // Prepare pros data
    const prosData = pros?.map((pro, index) => ({
      product_id: productId,
      title: pro?.title,
      description: pro?.description,
      is_pro: true,
      display_order: index + 1
    }));

    // Prepare cons data
    const consData = cons?.map((con, index) => ({
      product_id: productId,
      title: con?.title,
      description: con?.description,
      is_pro: false,
      display_order: index + 1
    }));

    // Insert all pros and cons
    const allData = [...prosData, ...consData];
    if (allData?.length > 0) {
      const { error } = await supabase
        ?.from('product_pros_cons')
        ?.insert(allData);

      if (error) throw error;
    }
  } catch (error) {
    console.error('Error saving product pros and cons:', error);
    throw error;
  }
}

/**
 * Checks if product already has AI-generated reviews
 * @param {string} productId - Product UUID
 * @returns {Promise<Object>} Existing review counts
 */
export async function checkExistingReviews(productId) {
  try {
    const { data, error } = await supabase
      ?.from('reviews')
      ?.select('id, is_ai_generated, reviewer_name, title')
      ?.eq('product_id', productId);

    if (error) throw error;

    const aiReviews = data?.filter(review => review?.is_ai_generated) || [];
    const humanReviews = data?.filter(review => !review?.is_ai_generated) || [];

    return {
      total: data?.length || 0,
      aiGenerated: aiReviews?.length,
      human: humanReviews?.length,
      hasExistingReviews: data?.length > 0,
      existingReviews: data || [],
    };
  } catch (error) {
    console.error('Error checking existing reviews:', error);
    throw error;
  }
}

/**
 * Saves AI-generated reviews to the database
 * @param {string} productId - Product UUID
 * @param {Array} reviews - Array of review objects
 * @returns {Promise<Array>} Array of saved review IDs
 */
export async function saveAIReviews(productId, reviews) {
  try {
    // Check for existing reviews first
    const existingData = await checkExistingReviews(productId);
    
    if (existingData?.hasExistingReviews) {
      console.warn(`Product ${productId} already has ${existingData?.total} reviews (${existingData?.aiGenerated} AI-generated)`);
    }

    const reviewsToInsert = reviews?.map(review => ({
      product_id: productId,
      reviewer_name: review?.reviewerName,
      avatar_url: generateAvatarUrl(review?.reviewerName),
      avatar_alt: `Profile picture of ${review?.reviewerName}`,
      rating: review?.rating,
      title: review?.title,
      content: review?.content,
      is_verified: true, // AI reviews are marked as verified
      helpful_count: Math.floor(Math.random() * 50), // Random initial helpful count
      is_ai_generated: true,
    }));

    const { data, error } = await supabase
      ?.from('reviews')
      ?.insert(reviewsToInsert)
      ?.select('id');

    if (error) throw error;

    return data?.map(item => item?.id);
  } catch (error) {
    console.error('Error saving AI reviews:', error);
    throw error;
  }
}

/**
 * Generates avatar URL from reviewer name using UI Avatars API
 * @param {string} name - Reviewer name
 * @returns {string} Avatar URL
 */
function generateAvatarUrl(name) {
  const initials = name
    ?.split(' ')
    ?.map(word => word?.[0])
    ?.join('')
    ?.toUpperCase();
  return `https://ui-avatars.com/api/?name=${encodeURIComponent(name)}&background=random&size=200&bold=true`;
}

/**
 * Generates affiliate link recommendations for a product
 * @param {Object} product - Product object
 * @returns {Promise<Object>} Affiliate link data
 */
export async function generateAffiliateLinks(product) {
  try {
    const prompt = `Generate affiliate shopping links for the following product.
    Provide links for major retailers that would typically carry this product.
    
    Product: ${product?.name} by ${product?.brand}
    Category: ${product?.category}
    
    Generate realistic affiliate links for:
    - Amazon
    - Best Buy
    - Target
    - Walmart
    - Manufacturer's website
    
    For each retailer, provide:
    - Retailer name
    - Affiliate link (use format: https://retailer.com/product-slug?ref=vacuumguide)
    - Current price (should be within ±10% of $${product?.currentPrice})
    - Availability status`;

    const response = await openai?.chat?.completions?.create({
      model: 'gpt-4o-mini',
      messages: [
        { 
          role: 'system', 
          content: 'You are a product sourcing expert who provides accurate retail information and affiliate links.' 
        },
        { role: 'user', content: prompt },
      ],
      response_format: {
        type: 'json_schema',
        json_schema: {
          name: 'affiliate_links',
          schema: {
            type: 'object',
            properties: {
              links: {
                type: 'array',
                items: {
                  type: 'object',
                  properties: {
                    retailer: { type: 'string' },
                    url: { type: 'string' },
                    price: { type: 'number' },
                    availability: { type: 'string', enum: ['in_stock', 'low_stock', 'out_of_stock'] },
                  },
                  required: ['retailer', 'url', 'price', 'availability'],
                },
              },
            },
            required: ['links'],
            additionalProperties: false,
          },
        },
      },
      verbosity: 'medium',
    });

    const affiliateData = JSON.parse(response?.choices?.[0]?.message?.content);
    return affiliateData;
  } catch (error) {
    console.error('Error generating affiliate links:', error);
    throw error;
  }
}

/**
 * Updates product with affiliate link data
 * @param {string} productId - Product UUID
 * @param {Object} affiliateData - Affiliate link data
 * @returns {Promise<void>}
 */
export async function updateProductAffiliateLinks(productId, affiliateData) {
  try {
    const { error } = await supabase
      ?.from('products')
      ?.update({
        affiliate_links: affiliateData?.links,
        updated_at: new Date()?.toISOString(),
      })
      ?.eq('id', productId);

    if (error) throw error;
  } catch (error) {
    console.error('Error updating product affiliate links:', error);
    throw error;
  }
}

/**
 * Complete workflow: Generate product review and affiliate links
 * @param {string} productId - Product UUID
 * @returns {Promise<Object>} Summary of generated content
 */
export async function generateProductContent(productId) {
  try {
    // Fetch product details
    const { data: product, error: fetchError } = await supabase
      ?.from('products')
      ?.select('*')
      ?.eq('id', productId)
      ?.single();

    if (fetchError) throw fetchError;

    // Convert snake_case to camelCase for AI processing
    const productData = {
      name: product?.name,
      brand: product?.brand,
      category: product?.category,
      currentPrice: product?.current_price,
      description: product?.description,
    };

    // Generate professional product review
    const reviewContent = await generateProductReview(productData);
    const savedReview = await saveProductReview(productId, reviewContent);

    // Generate affiliate links
    const affiliateData = await generateAffiliateLinks(productData);
    await updateProductAffiliateLinks(productId, affiliateData);

    return {
      success: true,
      reviewGenerated: true,
      isNewReview: savedReview?.isNew,
      affiliateLinksGenerated: affiliateData?.links?.length,
      review: reviewContent,
      affiliateLinks: affiliateData?.links,
    };
  } catch (error) {
    console.error('Error in generateProductContent:', error);
    throw error;
  }
}

/**
 * AI-powered product discovery and suggestions
 * @param {number} count - Number of products to suggest
 * @returns {Promise<Array>} Array of suggested product data
 */
export async function suggestPopularProducts(count = 5) {
  try {
    const prompt = `Suggest ${count} currently popular and highly-rated vacuum cleaner models that would be excellent candidates for detailed reviews. 
    
    For each vacuum, provide:
    - Product name (specific model)
    - Brand name
    - Category (upright, robot, cordless, canister, handheld, stick)
    - Price range (estimated retail price in USD)
    - Key features (3-5 bullet points)
    - Target audience (who this vacuum is best for)
    - Popularity score (1-10 based on market trends)
    - Brief description (50-100 words)
    
    Focus on:
    - Current market leaders and trending models
    - Mix of price ranges (budget, mid-range, premium)
    - Different vacuum types and use cases
    - Products with strong consumer interest
    - Models that would generate engaging reviews
    
    Ensure variety in brands, price points, and vacuum types.`;

    const response = await openai?.chat?.completions?.create({
      model: 'gpt-4o',
      messages: [
        { 
          role: 'system', 
          content: 'You are a vacuum cleaner market analyst with expertise in consumer trends, product popularity, and market research. You provide data-driven recommendations based on current market conditions.' 
        },
        { role: 'user', content: prompt },
      ],
      response_format: {
        type: 'json_schema',
        json_schema: {
          name: 'product_suggestions',
          schema: {
            type: 'object',
            properties: {
              products: {
                type: 'array',
                items: {
                  type: 'object',
                  properties: {
                    name: { type: 'string' },
                    brand: { type: 'string' },
                    category: { type: 'string' },
                    priceRange: { type: 'string' },
                    keyFeatures: { 
                      type: 'array',
                      items: { type: 'string' }
                    },
                    targetAudience: { type: 'string' },
                    popularityScore: { type: 'integer', minimum: 1, maximum: 10 },
                    description: { type: 'string' },
                  },
                  required: ['name', 'brand', 'category', 'priceRange', 'keyFeatures', 'targetAudience', 'popularityScore', 'description'],
                },
              },
            },
            required: ['products'],
            additionalProperties: false,
          },
        },
      },
    });

    const suggestions = JSON.parse(response?.choices?.[0]?.message?.content);
    return suggestions?.products || [];
  } catch (error) {
    console.error('Error suggesting popular products:', error);
    throw error;
  }
}

/**
 * Saves AI-suggested products to the database
 * @param {Array} products - Array of product suggestion objects
 * @returns {Promise<Object>} Object with saved products and duplicates info
 */
export async function saveSuggestedProducts(products) {
  try {
    const savedProducts = [];
    const duplicates = [];

    for (const product of products) {
      // Parse price range to get current price
      const priceMatch = product?.priceRange?.match(/\$(\d+)/);
      const currentPrice = priceMatch ? parseFloat(priceMatch?.[1]) : 299.99;

      // Generate slug from product name
      const slug = `${product?.brand?.toLowerCase()}-${product?.name?.toLowerCase()}`?.replace(/[^a-z0-9]+/g, '-')?.replace(/^-|-$/g, '');

      // Check for duplicates
      const duplicateCheck = await checkDuplicateProduct(slug, product?.brand, product?.name);
      
      if (duplicateCheck?.isDuplicate) {
        duplicates?.push({
          name: product?.name,
          brand: product?.brand,
          reason: `Already exists as: ${duplicateCheck?.existingProduct?.name}`,
          matchType: duplicateCheck?.matchType,
        });
        continue; // Skip this product
      }

      // Insert product if not duplicate
      const productToInsert = {
        name: product?.name,
        brand: product?.brand,
        category: product?.category,
        current_price: currentPrice,
        original_price: currentPrice * 1.2, // Set original 20% higher
        description: product?.description,
        image_url: `https://images.unsplash.com/photo-1558317374-067fb5f30001?w=800&auto=format&fit=crop`,
        image_alt: `${product?.brand} ${product?.name} vacuum cleaner`,
        slug: slug,
        is_active: true,
        is_ai_suggested: true,
        ai_metadata: {
          keyFeatures: product?.keyFeatures,
          targetAudience: product?.targetAudience,
          popularityScore: product?.popularityScore,
          suggestedAt: new Date()?.toISOString(),
        },
      };

      const { data, error } = await supabase
        ?.from('products')
        ?.insert([productToInsert])
        ?.select('id, name, brand')
        ?.single();

      if (error) {
        console.error(`Error inserting product ${product?.name}:`, error);
        duplicates?.push({
          name: product?.name,
          brand: product?.brand,
          reason: error?.message,
        });
      } else {
        savedProducts?.push(data);
      }
    }

    return {
      savedProducts,
      duplicates,
      totalSaved: savedProducts?.length,
      totalDuplicates: duplicates?.length,
    };
  } catch (error) {
    console.error('Error saving suggested products:', error);
    throw error;
  }
}

/**
 * Complete workflow: Suggest products and optionally save them
 * @param {number} count - Number of products to suggest
 * @param {boolean} saveToDb - Whether to save suggestions to database
 * @returns {Promise<Object>} Suggestion results
 */
export async function discoverAndSuggestProducts(count = 5, saveToDb = false) {
  try {
    const suggestions = await suggestPopularProducts(count);
    
    let saveResult = null;
    if (saveToDb) {
      saveResult = await saveSuggestedProducts(suggestions);
      
      // Throw error if all products were duplicates
      if (saveResult?.totalSaved === 0 && saveResult?.totalDuplicates > 0) {
        const duplicateList = saveResult?.duplicates?.map(d => `${d?.name} by ${d?.brand}`)?.join(', ');
        throw new Error(`All suggested products already exist in database: ${duplicateList}`);
      }
    }

    return {
      success: true,
      suggestionsCount: suggestions?.length,
      suggestions,
      savedProducts: saveToDb ? saveResult?.savedProducts : null,
      duplicates: saveToDb ? saveResult?.duplicates : null,
      totalSaved: saveToDb ? saveResult?.totalSaved : 0,
      totalDuplicates: saveToDb ? saveResult?.totalDuplicates : 0,
    };
  } catch (error) {
    console.error('Error in discoverAndSuggestProducts:', error);
    throw error;
  }
}

/**
 * Checks if product already exists in database
 * @param {string} slug - Product slug
 * @param {string} brand - Product brand
 * @param {string} name - Product name
 * @returns {Promise<Object>} Duplicate check result
 */
export async function checkDuplicateProduct(slug, brand, name) {
  try {
    // Check by slug (most reliable)
    const { data: bySlug } = await supabase
      ?.from('products')
      ?.select('id, name, brand, slug')
      ?.eq('slug', slug)
      ?.maybeSingle();

    if (bySlug) {
      return {
        isDuplicate: true,
        existingProduct: bySlug,
        matchType: 'slug',
      };
    }

    // Check by brand + name combination
    const { data: byBrandName } = await supabase
      ?.from('products')
      ?.select('id, name, brand, slug')
      ?.ilike('brand', brand)
      ?.ilike('name', `%${name}%`)
      ?.maybeSingle();

    if (byBrandName) {
      return {
        isDuplicate: true,
        existingProduct: byBrandName,
        matchType: 'brand-name',
      };
    }

    return {
      isDuplicate: false,
      existingProduct: null,
    };
  } catch (error) {
    console.error('Error checking duplicate product:', error);
    throw error;
  }
}

/**
 * Saves a manually added product to the database
 * @param {Object} productData - Product information
 * @returns {Promise<Object>} Saved product data
 */
export async function saveManualProduct(productData) {
  try {
    // Generate slug
    const slug = `${productData?.brand?.toLowerCase()}-${productData?.name?.toLowerCase()}`
      ?.replace(/[^a-z0-9]+/g, '-')
      ?.replace(/^-|-$/g, '');

    // Check for duplicates
    const duplicateCheck = await checkDuplicateProduct(slug, productData?.brand, productData?.name);
    
    if (duplicateCheck?.isDuplicate) {
      throw new Error(`Product already exists: ${duplicateCheck?.existingProduct?.name} by ${duplicateCheck?.existingProduct?.brand}`);
    }

    // Insert product
    const { data, error } = await supabase
      ?.from('products')
      ?.insert([{
        name: productData?.name,
        brand: productData?.brand,
        category: productData?.category,
        current_price: parseFloat(productData?.currentPrice),
        original_price: parseFloat(productData?.originalPrice || productData?.currentPrice),
        description: productData?.description,
        image_url: productData?.imageUrl || 'https://images.unsplash.com/photo-1558317374-067fb5f30001?w=800&auto=format&fit=crop',
        image_alt: productData?.imageAlt || `${productData?.brand} ${productData?.name} vacuum cleaner`,
        slug: slug,
        badge: productData?.badge || null,
        discount: productData?.discount || 0,
        is_active: true,
        is_ai_suggested: false,
      }])
      ?.select('*')
      ?.single();

    if (error) throw error;

    return {
      success: true,
      product: data,
    };
  } catch (error) {
    console.error('Error saving manual product:', error);
    throw error;
  }
}

export default {
  generateProductReview,
  saveProductReview,
  saveAIReviews,
  checkExistingReviews,
  generateAffiliateLinks,
  updateProductAffiliateLinks,
  generateProductContent,
  suggestPopularProducts,
  saveSuggestedProducts,
  discoverAndSuggestProducts,
  checkDuplicateProduct,
  saveManualProduct,
};